import { useQuery } from "@tanstack/react-query"
import { Card, Divider, Typography } from "antd"
import type React from "react"

const { Text } = Typography
import { useEffect, useState } from "react"
import CollocationSearchForm from "./components/CollocationSearchForm"
import CollocationTable from "./components/CollocationTable"
import CollocationTableActions from "./components/CollocationTableActions"
import {
  collocationService,
  type CollocationSearchParams,
  type CollocationApiParams,
} from "@/services/dictionary/collocationApi"
import { PAGE_CONFIG, PAGINATION_CONFIG } from "@/constants/table"
import { useTableParams } from "@/hooks/useTableParams"

export const CollocationPage: React.FC = () => {
  const [searchParams, setSearchParams] = useTableParams<CollocationSearchParams>({
    ...PAGE_CONFIG,
    search: "",
  })
  const [total, setTotal] = useState<number>(0)

  const {
    data: result,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ["collocation_list", searchParams],
    queryFn: () => {
      const apiParams: CollocationApiParams = {
        page: searchParams.page,
        limit: searchParams.per_page,
        search: searchParams.search || undefined,
      }
      return collocationService.list(apiParams)
    },
  })

  useEffect(() => {
    if (result?.paginate.total) {
      setTotal(result.paginate.total)
    }
  }, [result?.paginate])

  const handleSearch = (values: Omit<CollocationSearchParams, "page" | "per_page">) => {
    setSearchParams({
      ...values,
      page: 1,
      per_page: searchParams.per_page,
    })
  }

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])

  const handleSelectionChange = (keys: React.Key[]) => {
    setSelectedRowKeys(keys)
  }

  const tableLoading = isFetching

  return (
    <div className="flex flex-col gap-2">
      <style>{`
        .text-primary {
          color: var(--ant-color-primary, #1677ff) !important;
        }
        .border-primary {
          border-color: var(--ant-color-primary, #1677ff) !important;
        }
      `}</style>
      <CollocationSearchForm
        onSearch={handleSearch}
        loading={isFetching}
        initialValues={{
          search: searchParams.search,
        }}
      />
      <Card
        className="flex min-h-0 grow flex-col"
        classNames={{ body: "flex grow" }}
        title={
          <div className="flex items-center">
            <h2>Danh sách Collocation</h2>
            <Divider orientation="vertical" />
            <Text type="secondary" className="text-sm!">{`${selectedRowKeys.length} mục đã được chọn`}</Text>
            <Divider orientation="vertical" />
            <CollocationTableActions onRefresh={refetch} loading={tableLoading} />
          </div>
        }
      >
        <CollocationTable
          data={result?.data || []}
          loading={tableLoading}
          selectedRowKeys={selectedRowKeys}
          onSelectionChange={handleSelectionChange}
          pagination={{
            pageSize: searchParams.per_page,
            current: searchParams.page,
            total,
            ...PAGINATION_CONFIG,
            onChange(page: number, pageSize: number) {
              setSearchParams({
                ...searchParams,
                page: page,
                per_page: pageSize,
              })
            },
          }}
        />
      </Card>
    </div>
  )
}

export default CollocationPage
